import { MinusIcon, TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DataTable,
  EmptyState,
  ErrorNotice,
  LoadingState,
  Panel,
  SearchInput,
  SegmentedControl,
  TrendChart,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { filterRows } from '@/lib/facet-table';
import { periodLabel } from '@/lib/report-window';
import { useLocalSort } from '@/lib/use-local-sort';
import { readPageSources, type SourceBreakdownRow, type SourcesSeriesBy } from '@/lib/pages';
import type { StatsInterval } from '@/lib/leads';

/**
 * GA4 traffic-sources view shared by the site-wide Pages screen and the
 * per-page detail screen: a stacked sessions-by-channel chart plus channel
 * and source tables with previous-window deltas — the "where did traffic
 * drop off from" panel.
 */

/** Signed percent change, null without a baseline. */
export function pctChange(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null || previous === 0) return null;
  return Math.round(((current - previous) / previous) * 1000) / 10;
}

/** Signed percent beside a value: green up, red down (or inverted). */
export function DeltaPct({
  current,
  previous,
  downIsGood = false,
}: {
  readonly current: number;
  readonly previous: number | null;
  readonly downIsGood?: boolean;
}): React.ReactElement | null {
  const pct = pctChange(current, previous);
  if (pct === null) return null;
  const improving = downIsGood ? pct < 0 : pct > 0;
  return (
    <span
      className={`ml-1 text-portal-micro font-bold tabular-nums ${
        pct === 0 ? 'text-portal-muted' : improving ? 'text-portal-success' : 'text-rose-700'
      }`}
    >
      {pct > 0 ? (<TrendingUpIcon aria-hidden className="inline size-3.5 shrink-0" />) : pct < 0 ? (<TrendingDownIcon aria-hidden className="inline size-3.5 shrink-0" />) : (<MinusIcon aria-hidden className="inline size-3.5 shrink-0" />)}{' '}
      {Math.abs(pct).toFixed(Math.abs(pct) % 1 === 0 ? 0 : 1)}%
    </span>
  );
}

function compactCount(value: number): string {
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

/** Sort accessors for the source tables — module-level so the hook memo holds. */
const SOURCE_ACCESSORS: Readonly<Record<string, (row: SourceBreakdownRow) => number | string | null>> = {
  key: (row) => row.key,
  sessions: (row) => row.sessions,
  previous: (row) => row.previousSessions,
  change: (row) => pctChange(row.sessions, row.previousSessions),
  conversions: (row) => row.conversions,
};

/** The source-row fields the search box matches. */
const SOURCE_SEARCH_FIELDS: ReadonlyArray<keyof SourceBreakdownRow> = ['key'];

/**
 * Channel/source rows with previous-window deltas. The search is local
 * state, not `?q=`: the panel shows two of these side by side, on pages that
 * have their own list search.
 */
function SourceTable({
  title,
  hint,
  rows,
  rangeLabel,
  searchLabel,
  searchPlaceholder,
}: {
  readonly title: string;
  readonly hint: string;
  readonly rows: readonly SourceBreakdownRow[];
  readonly rangeLabel: string;
  readonly searchLabel: string;
  readonly searchPlaceholder: string;
}): React.ReactElement {
  const [search, setSearch] = useState('');
  const { sorted, sort, onSort } = useLocalSort(rows, SOURCE_ACCESSORS, {
    key: 'sessions',
    direction: 'desc',
  });
  const shown = filterRows(sorted, search, SOURCE_SEARCH_FIELDS);
  return (
    <Panel>
      <div className="px-5 pt-5 pb-4">
        <h3 className="text-portal-card-title font-bold text-portal-ink">{title}</h3>
        <p className="text-portal-micro text-portal-muted">
          {hint} Change is vs the previous {rangeLabel}.
        </p>
        {rows.length > 0 ? (
          <SearchInput
            label={searchLabel}
            placeholder={searchPlaceholder}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="mt-3 w-full max-w-sm"
          />
        ) : null}
      </div>
      {rows.length === 0 ? (
        <p className="px-5 pb-5 text-portal-micro text-portal-muted">Nothing in this range.</p>
      ) : shown.length === 0 ? (
        <div className="px-5 pb-5">
          <EmptyState title="No rows match" message="Try a different search." />
        </div>
      ) : (
        <div className="max-h-[360px] overflow-y-auto rounded-b-[8px]">
          <DataTable
            headers={[
              { label: 'Segment', sortKey: 'key' },
              { label: 'Sessions', sortKey: 'sessions' },
              { label: 'Previous', sortKey: 'previous' },
              { label: 'Change', sortKey: 'change' },
              { label: 'Leads', sortKey: 'conversions' },
            ]}
            visuallyHiddenHeaders={[0]}
            rightAlignedHeaders={[1, 2, 3, 4]}
            minWidth={320}
            flush
            sort={sort}
            onSort={onSort}
            // On a phone the five columns still pan inside a half-width panel: cards instead.
            cards={shown.map((row) => ({
              key: row.key,
              title: row.key,
              fields: [
                { label: 'Sessions', value: row.sessions.toLocaleString() },
                { label: 'Previous', value: row.previousSessions.toLocaleString() },
                { label: 'Change', value: <DeltaPct current={row.sessions} previous={row.previousSessions} /> },
                { label: 'Leads', value: row.conversions.toLocaleString() },
              ],
            }))}
          >
            {shown.map((row) => (
              <tr key={row.key} className="border-t border-portal-line align-middle">
                <td className="p-3 text-portal-small font-bold text-portal-ink">{row.key}</td>
                <td className="p-3 text-right text-portal-small tabular-nums">{row.sessions.toLocaleString()}</td>
                <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">
                  {row.previousSessions.toLocaleString()}
                </td>
                <td className="p-3 text-right text-portal-small whitespace-nowrap">
                  <DeltaPct current={row.sessions} previous={row.previousSessions} />
                </td>
                <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">
                  {row.conversions.toLocaleString()}
                </td>
              </tr>
            ))}
          </DataTable>
        </div>
      )}
    </Panel>
  );
}

const SERIES_BY_OPTIONS: readonly { key: SourcesSeriesBy; label: string }[] = [
  { key: 'channel', label: 'Channels' },
  { key: 'source', label: 'Sources' },
];

/** Live lookups re-ask Google at most every five minutes. */
const SOURCES_STALE_MS = 5 * 60 * 1000;

export interface TrafficSourcesPanelProps {
  readonly hint: string;
  readonly rangeLabel: string;
  readonly from: string;
  /** Exclusive. */
  readonly to: string;
  readonly interval: StatsInterval;
  /** One page's sources; absent = site-wide. */
  readonly path?: string;
  /** Site-wide only: count hidden pages too (the Pages screen's "Include hidden pages" switch). */
  readonly includeHidden?: boolean;
}

/**
 * The full panel: sessions-over-time split by channel OR by top referrer
 * (the "Trend by" toggle), plus channel/source tables with previous-window
 * deltas. Owns its own live GA4 query.
 */
export function TrafficSourcesPanel({
  hint,
  rangeLabel,
  from,
  to,
  interval,
  path,
  includeHidden = false,
}: TrafficSourcesPanelProps): React.ReactElement {
  const [seriesBy, setSeriesBy] = useState<SourcesSeriesBy>('channel');
  const query = useQuery({
    queryKey: ['page-sources', path ?? 'site', from, to, interval, seriesBy, includeHidden],
    queryFn: ({ signal }) =>
      readPageSources(
        { from, to, interval, seriesBy, ...(path !== undefined ? { path } : {}), ...(includeHidden ? { includeHidden } : {}) },
        signal,
      ),
    staleTime: SOURCES_STALE_MS,
  });
  const stats = query.data;
  return (
    <Panel className="mt-4 p-5">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-portal-card-title font-bold text-portal-ink">Traffic sources</h2>
          <p className="text-portal-micro text-portal-muted">{hint}</p>
        </div>
        <SegmentedControl<SourcesSeriesBy>
          label="Trend by"
          value={seriesBy}
          onChange={setSeriesBy}
          options={SERIES_BY_OPTIONS.map((option) => ({ key: option.key, label: option.label }))}
        />
      </div>
      {query.isPending ? (
        <LoadingState label="Asking Google Analytics…" />
      ) : query.isError || stats === undefined ? (
        <ErrorNotice title="Sources unavailable" message={errorMessage(query.error)} />
      ) : stats.channels.length === 0 ? (
        <EmptyState title="No sessions in this range" message="Try a wider range." />
      ) : (
        <>
          <TrendChart
            points={stats.series.periods.map((period, index) => ({
              key: period,
              label: periodLabel(period, stats.range.interval),
              value: stats.series.perKey.reduce(
                (sum, entry) => sum + (entry.values[index] ?? 0),
                0,
              ),
              details: [],
            }))}
            series={stats.series.perKey.map((entry) => ({
              key: entry.key,
              label: entry.key,
              values: [...entry.values],
            }))}
            variant="bar"
            title={seriesBy === 'channel' ? 'Sessions by channel' : 'Sessions by source'}
            unit="sessions"
            formatValue={compactCount}
          />
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <SourceTable
              title="By channel"
              hint="GA4 default channel groups."
              rows={stats.channels}
              rangeLabel={rangeLabel}
              searchLabel="Search channels"
              searchPlaceholder="Channel"
            />
            <SourceTable
              title="By source"
              hint="The site or engine that referred the session."
              rows={stats.sources}
              rangeLabel={rangeLabel}
              searchLabel="Search sources"
              searchPlaceholder="Source"
            />
          </div>
        </>
      )}
    </Panel>
  );
}
